/**
 * Source template — NOT registered.
 *
 * The registry's glob (`./*\/index.ts`) deliberately excludes folders whose
 * name starts with `_`. This file is here only as a reference.
 *
 * To add a new source:
 *   1. Copy this folder to `src/sources/<your-id>/`.
 *   2. Rename it (remove the leading underscore).
 *   3. Replace `_template` everywhere in the file below with your source id.
 *   4. Implement each `TODO` and remove the stubs.
 *   5. Update the `id` union in `src/sources/types.ts` if you're adding a
 *      brand-new id.
 *
 * The registry will pick it up on next reload. No central edits required.
 */

import type {
  ComicCard,
  ComicDetails,
  ComicSource,
  ListOpts,
  Page,
  PageRef,
  PopularOpts,
  SearchQuery,
  SourceCommentOpts,
  SourceCommentPage,
  SourceAuth,
} from '../types';

// import { proxyFetch } from '@/lib/proxy';
// import { parseDocument } from '@/lib/html';
// import { serializeCookies } from '@/lib/cookies';

const NOT_IMPLEMENTED = '[_template] not implemented — copy this folder to start a real source.';

async function search(_q: SearchQuery, _auth?: SourceAuth): Promise<Page<ComicCard>> {
  // TODO: call source search endpoint via proxyFetch, parse, return a Page<ComicCard>.
  throw new Error(NOT_IMPLEMENTED);
}

async function getPopular(_opts?: PopularOpts, _auth?: SourceAuth): Promise<Page<ComicCard>> {
  // TODO: call source "popular" or "front page" endpoint, parse, return a Page<ComicCard>.
  throw new Error(NOT_IMPLEMENTED);
}

async function getComic(_id: string, _auth?: SourceAuth): Promise<ComicDetails> {
  // TODO: fetch the gallery detail page/JSON, parse, return a ComicDetails.
  throw new Error(NOT_IMPLEMENTED);
}

async function getComments(
  _id: string,
  _auth?: SourceAuth,
  _opts?: SourceCommentOpts,
): Promise<SourceCommentPage> {
  // OPTIONAL: if `capabilities.comments` is true, fetch read-only upstream
  // comments lazily and return a SourceCommentPage.
  throw new Error(NOT_IMPLEMENTED);
}

async function getPages(_id: string, _auth?: SourceAuth): Promise<PageRef[]> {
  // TODO: return one PageRef per page. Either include `resolvedUrl` directly
  // or stash a token in `extra` to resolve lazily in `resolveImageUrl`.
  throw new Error(NOT_IMPLEMENTED);
}

async function resolveImageUrl(
  _ref: PageRef,
  _auth?: SourceAuth,
): Promise<{ url: string }> {
  // TODO: return the actual CDN URL. Image bytes go straight to <img src>;
  // do not proxy them.
  throw new Error(NOT_IMPLEMENTED);
}

async function validateAuth(_auth: SourceAuth): Promise<{ ok: boolean; username?: string }> {
  // TODO: hit an authenticated endpoint (e.g. /favorites or /uconfig) and
  // assert the response shape indicates a logged-in user.
  return { ok: false };
}

async function listFavorites(_auth: SourceAuth, _opts: ListOpts): Promise<Page<ComicCard>> {
  throw new Error(NOT_IMPLEMENTED);
}

async function addFavorite(_id: string, _auth: SourceAuth, _category?: number): Promise<void> {
  throw new Error(NOT_IMPLEMENTED);
}

async function removeFavorite(_id: string, _auth: SourceAuth): Promise<void> {
  throw new Error(NOT_IMPLEMENTED);
}

/**
 * NOTE: this object's `id` is intentionally one of the existing SourceId
 * union members so this file type-checks. When you copy it, change the id
 * to your real source id (and add it to the union in `types.ts` if needed).
 */
const source: ComicSource = {
  id: 'hitomi',
  name: '_template (not registered)',
  description: 'Reference implementation. Copy this folder to start a real source.',
  capabilities: {
    login: false,
    favorites: false,
    search: false,
    // comments: true,
  },
  search,
  getPopular,
  getComic,
  getComments,
  getPages,
  resolveImageUrl,
  validateAuth,
  listFavorites,
  addFavorite,
  removeFavorite,
};

export default source;
